import React, { createContext, useContext, useState } from "react";

/**
 * The signed-in user's WorkOS organization (station) id, decoded from the
 * access token's org_id claim. Pure dependency injection: AuthTokenBridge is
 * the SOLE writer (sets it whenever the user changes, nulls it on logout);
 * everything else reads via useOrgId(). Station-scoped queries gate on
 * `enabled: !!organizationId`, so a change or logout naturally stops them.
 *
 * Reader and writer live in separate contexts so the bridge (writer) never
 * re-renders with every org change and readers can't accidentally write.
 */
const OrgIdContext = createContext<string | null>(null);
const SetOrgIdContext = createContext<(id: string | null) => void>(() => {});

export function OrgIdProvider({ children }: { children: React.ReactNode }) {
  const [orgId, setOrgId] = useState<string | null>(null);
  return (
    <SetOrgIdContext.Provider value={setOrgId}>
      <OrgIdContext.Provider value={orgId}>{children}</OrgIdContext.Provider>
    </SetOrgIdContext.Provider>
  );
}

export function useOrgId(): string | null {
  return useContext(OrgIdContext);
}

/** Writer hook — AuthTokenBridge is the only sanctioned caller. */
export function useSetOrgId(): (id: string | null) => void {
  return useContext(SetOrgIdContext);
}
